import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { contractsAPI, usersAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { FileSignature, Search, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };
const fmt = (n, code) =>
  `${CURRENCY_SYMBOLS[code] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';

const PRODUCT_LABELS = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro' };
const NAVY = 'hsl(var(--c4p-navy-900))';

const ProductBadge = ({ code, category }) => {
  if (category && category !== 'PRODUCTION') {
    const styles = {
      TEST_SYSTEM: { background: '#fff7ed', color: '#ea580c', border: '1px solid #fdba74' },
      BESPOKE:     { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
      MANUAL:      { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
    };
    const labels = { TEST_SYSTEM: 'Custom Test', BESPOKE: 'Custom', MANUAL: 'Custom' };
    return (
      <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
        style={styles[category] || {}}>
        {labels[category] || category}
      </span>
    );
  }
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={code === 'STANDARD'
        ? { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }
        : { background: NAVY, color: 'white' }}>
      {PRODUCT_LABELS[code] || code}
    </span>
  );
};

// Shown when contract was created with manually-set pricing (not system derived)
const ManualPricingBadge = () => (
  <span className="text-xs font-medium px-1.5 py-0.5 rounded whitespace-nowrap"
    style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
    Manual Pricing
  </span>
);

const ContractStatusBadge = ({ status }) => {
  const styles = {
    LIVE:       { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' },
    ARCHIVED:   { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
    TERMINATED: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  };
  const s = styles[status] || styles.LIVE;
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {status}
    </span>
  );
};

const BillingLabel = ({ billingModel }) => {
  const labels = {
    TENANT_DIRECT: 'Tenant Direct',
    DIRECT:        'Direct',
    PARTNER:       'Partner',
  };
  return (
    <span className="text-xs whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
      {labels[billingModel] || billingModel || '\u2014'}
    </span>
  );
};

const ContractEmptyState = ({ hasFilters }) => (
  <TableRow>
    <TableCell colSpan={12} className="text-center py-12">
      <FileSignature size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
        {hasFilters ? 'No contracts match your filters.' : 'No contracts yet.'}
      </p>
      <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
        Contracts are created automatically when a quote is signed.
      </p>
    </TableCell>
  </TableRow>
);

const ContractTableRow = ({ contract, onClick }) => (
  <TableRow
    className="data-table-row cursor-pointer"
    onClick={onClick}
    data-testid={`contract-row-${contract.id}`}
  >
    <TableCell>
      <span className="font-mono text-sm font-semibold" style={{ color: NAVY }}>
        {contract.contract_number}
      </span>
    </TableCell>
    <TableCell><BillingLabel billingModel={contract.billing_model} /></TableCell>
    <TableCell className="text-sm max-w-[150px] truncate">{contract.customer_name || '\u2014'}</TableCell>
    <TableCell className="text-sm max-w-[150px] truncate" style={{ color: 'hsl(var(--muted-fg))' }}>
      {contract.tenant_name || '\u2014'}
    </TableCell>
    <TableCell>
      <ProductBadge code={contract.product_code} category={contract.quote_category} />
    </TableCell>
    <TableCell>
      {contract.contract_source === 'manual' && <ManualPricingBadge />}
    </TableCell>
    <TableCell className="text-sm tabular-nums">{contract.num_users ?? '\u2014'}</TableCell>
    <TableCell className="text-sm font-semibold tabular-nums" style={{ color: NAVY }}>
      {contract.monthly_total != null ? fmt(contract.monthly_total, contract.currency) + '/mo' : '\u2014'}
    </TableCell>
    <TableCell><ContractStatusBadge status={contract.status} /></TableCell>
    <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
      {contract.psm_user_name || '\u2014'}
    </TableCell>
    <TableCell className="text-sm whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
      {fmtDate(contract.created_at)}
    </TableCell>
    <TableCell>
      <ChevronRight size={15} style={{ color: 'hsl(var(--muted-fg))' }} />
    </TableCell>
  </TableRow>
);

const ContractsFilterBar = ({ search, onSearch, statusFilter, onStatus, productFilter, onProduct,
  psmFilter, onPsm, dateFrom, onDateFrom, dateTo, onDateTo, canManage, showAllPSMs, psmUsers, hasFilters, onClear }) => (
  <div className="space-y-2">
    <div className="flex gap-2 flex-wrap items-center">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
        <Input
          placeholder="Search by number, name, or partner..."
          value={search}
          onChange={e => onSearch(e.target.value)}
          className="pl-9"
          data-testid="contracts-search-input"
        />
      </div>
      <Select value={statusFilter || 'all'} onValueChange={v => onStatus(v === 'all' ? '' : v)}>
        <SelectTrigger className="w-36"><SelectValue placeholder="All statuses" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="LIVE">Live</SelectItem>
          <SelectItem value="ARCHIVED">Archived</SelectItem>
          <SelectItem value="TERMINATED">Terminated</SelectItem>
        </SelectContent>
      </Select>
      <Select value={productFilter || 'all'} onValueChange={v => onProduct(v === 'all' ? '' : v)}>
        <SelectTrigger className="w-36"><SelectValue placeholder="All products" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All products</SelectItem>
          <SelectItem value="STANDARD">Standard</SelectItem>
          <SelectItem value="ADVANCED">Advanced</SelectItem>
          <SelectItem value="MTPRO">MTPro</SelectItem>
        </SelectContent>
      </Select>
      {canManage && showAllPSMs && psmUsers.length > 0 && (
        <Select value={psmFilter || 'all'} onValueChange={v => onPsm(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All PSMs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All PSMs</SelectItem>
            {psmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
    <div className="flex gap-2 flex-wrap items-center">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-fg))' }}>From:</span>
        <Input type="date" value={dateFrom} onChange={e => onDateFrom(e.target.value)}
          className="w-36 h-8 text-sm" data-testid="contracts-date-from" />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium" style={{ color: 'hsl(var(--muted-fg))' }}>To:</span>
        <Input type="date" value={dateTo} onChange={e => onDateTo(e.target.value)}
          className="w-36 h-8 text-sm" data-testid="contracts-date-to" />
      </div>
      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={onClear} data-testid="contracts-clear-filters-btn">
          Clear all
        </Button>
      )}
    </div>
  </div>
);

export default function ContractsPage() {
  const { user, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const canManage = isAdmin || isManager;

  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('LIVE');
  const [productFilter, setProductFilter] = useState('');
  const [psmFilter, setPsmFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showMyContracts, setShowMyContracts] = useState(true);
  const [psmUsers, setPsmUsers] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    if (canManage) {
      usersAPI.list().then(r => {
        const users = Array.isArray(r.data) ? r.data : [];
        setPsmUsers(users.filter(u => u.is_active !== false));
      }).catch(() => {});
    }
  }, [canManage]);

  const handleSort = (field) => {
    if (sortBy === field) { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); }
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortTh = ({ field, children, className = '' }) => {
    const active = sortBy === field;
    return (
      <TableHead className={`cursor-pointer select-none hover:bg-muted/30 ${className}`}
        onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
        <div className="flex items-center gap-1">{children}
          {active ? (sortOrder === 'desc' ? <span style={{ fontSize:10,opacity:.7 }}>↓</span> : <span style={{ fontSize:10,opacity:.7 }}>↑</span>) : <span style={{ fontSize:10,opacity:.3 }}>↕</span>}
        </div>
      </TableHead>
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, sort_by: sortBy, sort_order: sortOrder };
      params.status = statusFilter || 'ALL';
      if (productFilter) params.product_code = productFilter;
      if (psmFilter)     params.psm_user_id  = psmFilter;
      if (dateFrom)      params.date_from    = dateFrom;
      if (dateTo)        params.date_to      = dateTo;
      if (search.trim()) params.search       = search.trim();
      if (showMyContracts && canManage && user?.id) params.psm_user_id = user.id;
      const { data } = await contractsAPI.list(params);
      setContracts(data.contracts || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, productFilter, psmFilter, dateFrom, dateTo, showMyContracts, canManage, user?.id, sortBy, sortOrder]);

  const hasFilters = search || (statusFilter && statusFilter !== 'LIVE') || productFilter || psmFilter || dateFrom || dateTo;

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="contracts-page">

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
              {showMyContracts ? 'My Contracts' : 'All Contracts'}
            </h2>
            {/* My / All toggle */}
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg" style={{ background: '#e2e8f0' }}>
              <button
                onClick={() => { setShowMyContracts(true); setPsmFilter(''); }}
                className="px-3 py-1 text-xs font-medium rounded-md transition-all outline-none"
                style={{
                  background: showMyContracts ? 'white' : 'transparent',
                  color: showMyContracts ? NAVY : '#64748b',
                  boxShadow: showMyContracts ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
                data-testid="my-contracts-toggle"
              >
                My Contracts
              </button>
              <button
                onClick={() => { setShowMyContracts(false); setPsmFilter(''); }}
                className="px-3 py-1 text-xs font-medium rounded-md transition-all outline-none"
                style={{
                  background: !showMyContracts ? 'white' : 'transparent',
                  color: !showMyContracts ? NAVY : '#64748b',
                  boxShadow: !showMyContracts ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                }}
                data-testid="all-contracts-toggle"
              >
                All Contracts
              </button>
            </div>
          </div>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            {loading ? '...' : `${total} ${showMyContracts && canManage ? 'of your' : ''} contract${total !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <ContractsFilterBar
        search={search} onSearch={setSearch}
        statusFilter={statusFilter} onStatus={setStatusFilter}
        productFilter={productFilter} onProduct={setProductFilter}
        psmFilter={psmFilter} onPsm={setPsmFilter}
        dateFrom={dateFrom} onDateFrom={setDateFrom}
        dateTo={dateTo} onDateTo={setDateTo}
        canManage={canManage}
        showAllPSMs={!showMyContracts}
        psmUsers={psmUsers}
        hasFilters={hasFilters}
        onClear={() => { setSearch(''); setStatusFilter('LIVE'); setProductFilter(''); setPsmFilter(''); setDateFrom(''); setDateTo(''); }}
      />

      {/* Desktop table */}
      <div className="hidden md:block rounded-xl border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <SortTh field="contract_number">Contract #</SortTh>
                <TableHead>Billing</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead>Users</TableHead>
                <SortTh field="monthly_total">Monthly</SortTh>
                <SortTh field="status">Status</SortTh>
                <TableHead>PSM</TableHead>
                <SortTh field="created_at">Created</SortTh>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={12}><Skeleton className="h-10 w-full" /></TableCell>
                  </TableRow>
                ))
              ) : contracts.length === 0 ? (
                <ContractEmptyState hasFilters={hasFilters} />
              ) : (
                contracts.map(c => (
                  <ContractTableRow
                    key={c.id}
                    contract={c}
                    onClick={() => navigate(`/contracts/${c.id}`)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile card list (< md) */}
      <div className="md:hidden space-y-2">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : contracts.length === 0 ? (
          <div className="text-center py-12 rounded-xl border bg-white">
            <FileSignature size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No contracts yet.</p>
          </div>
        ) : (
          contracts.map(c => (
            <div key={c.id}
              onClick={() => navigate(`/contracts/${c.id}`)}
              className="rounded-xl border bg-white p-4 cursor-pointer active:bg-gray-50"
              data-testid={`contract-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-bold" style={{ color: NAVY }}>{c.contract_number}</span>
                    <ContractStatusBadge status={c.status} />
                    <ProductBadge code={c.product_code} category={c.quote_category} />
                  </div>
                  <p className="text-xs mt-1 truncate font-medium" style={{ color: NAVY }}>{c.contract_name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                    {c.customer_name || '-'}{c.tenant_name ? ` · ${c.tenant_name}` : ''} · {c.num_users} users
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-sm" style={{ color: NAVY }}>
                    {c.monthly_total != null ? fmt(c.monthly_total, c.currency) + '/mo' : '-'}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(c.created_at)}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
}
